import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Activity,
  ArrowLeft,
  Building2,
  CheckCircle,
  Clock,
  Edit,
  Eye,
  Lock,
  Mail,
  Phone,
  Save,
  Shield,
  ShieldCheck,
  User,
  XCircle,
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';
import {
  applyUserOverrides,
  getModuleSelectionMap,
  loadModuleCatalog,
  ModuleCatalogItem,
} from '../../utils/moduleAccessAdmin';

interface OverrideEntry {
  permission_key: string;
  effect: string;
}

const UserEdit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth() as any;
  const isViewMode = !location.pathname.includes('/edit');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingModuleConfig, setLoadingModuleConfig] = useState(false);
  const [resolvedSiteMeta, setResolvedSiteMeta] = useState<{ site_name?: string; site_code?: string } | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogItem[]>([]);
  const [companyAllowedModuleKeys, setCompanyAllowedModuleKeys] = useState<string[]>([]);
  const [siteAllowedModuleKeys, setSiteAllowedModuleKeys] = useState<string[]>([]);
  const [effectiveModuleKeys, setEffectiveModuleKeys] = useState<string[]>([]);
  const [overrideDeniedModuleKeys, setOverrideDeniedModuleKeys] = useState<string[]>([]);
  const [userOverrideEntries, setUserOverrideEntries] = useState<OverrideEntry[]>([]);
  const [applyCustomUserModuleAccess, setApplyCustomUserModuleAccess] = useState(false);
  const [moduleAccessSource, setModuleAccessSource] = useState<'system' | 'company' | 'site' | 'user' | 'none'>('none');
  const [userModuleSelection, setUserModuleSelection] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    course_role: '',
    site_id: '',
    company_id: '',
    is_active: true,
    country_code: '',
    phone_number: '',
    new_password: '',
    confirm_new_password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const normalizedRole = String(formData.role || '').toLowerCase();
  const roleRequiresSite = ['site_admin', 'general_user', 'user', 'admin'].includes(normalizedRole);
  const isHoScopedRole = ['ho_admin', 'company_admin'].includes(normalizedRole);
  const isGeneralUserRole = normalizedRole === 'general_user';
  const canEditCompany =
    !!currentUser?.isGlobalAdmin ||
    currentUser?.role === 'system_admin' ||
    currentUser?.role === 'super_admin' ||
    currentUser?.role === 'admin';

  const availableModuleKeys = isHoScopedRole
    ? companyAllowedModuleKeys
    : roleRequiresSite
      ? siteAllowedModuleKeys
      : companyAllowedModuleKeys;

  const availableUserModules = moduleCatalog.filter(
    (module) => availableModuleKeys.includes(module.module_key)
  );

  const buildSelectionMap = (allowedKeys: string[], selectedKeys: string[] = []) => {
    const fallbackKeys = selectedKeys.length > 0 ? selectedKeys : allowedKeys;
    const baseMap = getModuleSelectionMap(moduleCatalog, fallbackKeys);
    const next: Record<string, boolean> = {};

    moduleCatalog.forEach((module) => {
      if (allowedKeys.includes(module.module_key)) {
        next[module.module_key] = !!baseMap[module.module_key];
      }
    });

    return next;
  };

  const formatDate = (value?: string) => {
    if (!value) return 'Not available';
    try {
      return new Date(value).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return value;
    }
  };

  const isAccountLocked = () => {
    if (!userData?.account_locked_until) return false;
    return new Date(userData.account_locked_until) > new Date();
  };

  const fetchRoles = async () => {
    try {
      const response = await backendApi.getRoles();
      if (response.success && response.roles) {
        setRoles(response.roles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await backendApi.getSites(false);
      if (response.success && response.sites) {
        setSites(response.sites);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await backendApi.getCompanies();
      if (response.success && response.companies) {
        setCompanies(response.companies);
      } else if (currentUser?.companyId) {
        setCompanies([{
          company_id: currentUser.companyId,
          company_name: currentUser.company || 'Assigned Company',
          company_code: String(currentUser.companyId),
        }]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      if (currentUser?.companyId) {
        setCompanies([{
          company_id: currentUser.companyId,
          company_name: currentUser.company || 'Assigned Company',
          company_code: String(currentUser.companyId),
        }]);
      }
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await backendApi.getUser(Number(id));
      if (!response.success || !response.user) {
        throw new Error(response.error || 'Failed to load user');
      }

      const loadedUser = response.user;
      setUserData(loadedUser);

      let userRole = loadedUser.role_name || loadedUser.role || 'general_user';
      if (userRole === 'user') userRole = 'general_user';
      if (userRole === 'admin') userRole = 'site_admin';
      if (!loadedUser.role_name) {
        if (loadedUser.role === 'admin') {
          userRole = loadedUser.is_superuser ? 'system_admin' : 'ho_admin';
        } else if (['user', 'viewer'].includes(loadedUser.role)) {
          userRole = 'general_user';
        }
      }

      let companyId = '';
      if (loadedUser.company_id) companyId = String(loadedUser.company_id);
      else if (loadedUser.company?.company_id) companyId = String(loadedUser.company.company_id);
      else if (loadedUser.site?.company_id) companyId = String(loadedUser.site.company_id);

      let siteId = '';
      if (loadedUser.site?.site_id) siteId = String(loadedUser.site.site_id);
      else if (loadedUser.site_id) siteId = String(loadedUser.site_id);
      else if (typeof loadedUser.site === 'number') siteId = String(loadedUser.site);

      setFormData({
        username: loadedUser.username || '',
        email: loadedUser.email || '',
        first_name: loadedUser.first_name || loadedUser.firstName || '',
        last_name: loadedUser.last_name || loadedUser.lastName || '',
        role: userRole,
        course_role: loadedUser.role || '',
        site_id: siteId,
        company_id: companyId,
        is_active: loadedUser.is_active !== false,
        country_code: loadedUser.country_code || '',
        phone_number: loadedUser.phone_number || loadedUser.phone || '',
        new_password: '',
        confirm_new_password: '',
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      alert('Failed to load user');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyModuleAccess = async (companyId: string | null): Promise<string[]> => {
    if (!companyId) {
      setCompanyAllowedModuleKeys([]);
      return [];
    }

    try {
      const response = await backendApi.getNavigationSubscriptions(companyId);
      if (!response.success) {
        setCompanyAllowedModuleKeys([]);
        return [];
      }

      const activeKeys: string[] = Array.from(new Set<string>(
        (response.subscriptions || [])
          .filter((item: any) => String(item?.status || '').toLowerCase() === 'active')
          .map((item: any) => String(item?.module_key || '').trim())
          .filter((key: string) => key.length > 0)
      ));

      setCompanyAllowedModuleKeys(activeKeys);
      return activeKeys;
    } catch (error) {
      console.error('Failed to load company module access:', error);
      setCompanyAllowedModuleKeys([]);
      return [];
    }
  };

  const loadSiteModuleAccess = async (siteId: string | null, companyId: string | null, roleNameForPolicy: string, fallbackKeys: string[]): Promise<string[]> => {
    if (!siteId || !companyId) {
      setSiteAllowedModuleKeys([]);
      return [];
    }

    try {
      const response = await backendApi.getNavigationRolePolicies({
        companyId,
        siteId,
        scopeLevel: 'site',
        roleName: roleNameForPolicy,
      });

      const nextKeys = response.success && Array.isArray(response.allowed_module_keys) && response.allowed_module_keys.length > 0
        ? response.allowed_module_keys
        : fallbackKeys;

      setSiteAllowedModuleKeys(nextKeys);
      return nextKeys;
    } catch (error) {
      console.error('Failed to load site module access:', error);
      setSiteAllowedModuleKeys(fallbackKeys);
      return fallbackKeys;
    }
  };

  const loadUserOverrideAccess = async (userId: number, companyId: string | null, siteId: string | null) => {
    if (!userId) {
      setOverrideDeniedModuleKeys([]);
      setUserOverrideEntries([]);
      return { allowed: [] as string[], denied: [] as string[], entries: [] as OverrideEntry[] };
    }

    try {
      const response = await backendApi.getNavigationUserOverrides({
        userId,
        companyId,
        siteId,
        scopeLevel: 'user',
      });

      const allowed = Array.isArray(response.allowed_module_keys) ? response.allowed_module_keys : [];
      const denied = Array.isArray(response.denied_module_keys) ? response.denied_module_keys : [];
      const entries: OverrideEntry[] = Array.isArray(response.overrides)
        ? response.overrides
          .filter((item: any) => String(item?.permission_key || '').endsWith('.view'))
          .map((item: any) => ({
            permission_key: String(item?.permission_key || ''),
            effect: String(item?.effect || 'allow'),
          }))
        : [];

      setOverrideDeniedModuleKeys(denied);
      setUserOverrideEntries(entries);
      return { allowed, denied, entries };
    } catch (error) {
      console.error('Failed to load user override access:', error);
      setOverrideDeniedModuleKeys([]);
      setUserOverrideEntries([]);
      return { allowed: [] as string[], denied: [] as string[], entries: [] as OverrideEntry[] };
    }
  };

  const refreshModuleAccess = async () => {
    if (moduleCatalog.length === 0) {
      return;
    }

    setLoadingModuleConfig(true);
    try {
      const companyKeys = await loadCompanyModuleAccess(formData.company_id || null);
      let inheritedKeys: string[] = companyKeys;
      let source: 'system' | 'company' | 'site' | 'user' | 'none' = companyKeys.length > 0 ? 'company' : 'none';

      if (roleRequiresSite) {
        const policyRoleName = isGeneralUserRole ? 'general_user' : 'site_admin';
        inheritedKeys = await loadSiteModuleAccess(formData.site_id || null, formData.company_id || null, policyRoleName, companyKeys);
        source = inheritedKeys.length > 0 ? 'site' : source;
      } else {
        setSiteAllowedModuleKeys([]);
      }

      if (['system_admin', 'super_admin'].includes(normalizedRole) && inheritedKeys.length === 0) {
        inheritedKeys = moduleCatalog.map((module) => module.module_key);
        source = 'system';
      }

      let finalKeys = [...inheritedKeys];
      let nextApplyCustom = false;

      if (isGeneralUserRole && id) {
        const overrideState = await loadUserOverrideAccess(Number(id), formData.company_id || null, formData.site_id || null);
        if (overrideState.allowed.length > 0 || overrideState.denied.length > 0) {
          const merged = new Set(inheritedKeys);
          overrideState.allowed.forEach((key: string) => merged.add(key));
          overrideState.denied.forEach((key: string) => merged.delete(key));
          finalKeys = Array.from(merged);
          nextApplyCustom = true;
          source = 'user';
        }
      } else {
        setOverrideDeniedModuleKeys([]);
        setUserOverrideEntries([]);
      }

      setApplyCustomUserModuleAccess(nextApplyCustom);
      setModuleAccessSource(source);
      setEffectiveModuleKeys(finalKeys);
      setUserModuleSelection(buildSelectionMap(inheritedKeys, finalKeys));
    } finally {
      setLoadingModuleConfig(false);
    }
  };

  useEffect(() => {
    void fetchRoles();
    void fetchSites();
    void fetchCompanies();
    if (id) {
      void fetchUser();
    }
  }, [id]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const modules = await loadModuleCatalog(backendApi);
        setModuleCatalog(modules);
        setUserModuleSelection(getModuleSelectionMap(modules));
      } catch (error) {
        console.error('Failed to load module catalog:', error);
      }
    };

    void loadMeta();
  }, []);

  useEffect(() => {
    const fetchSiteMetaById = async () => {
      if (!formData.site_id) {
        setResolvedSiteMeta(null);
        return;
      }
      if (sites.some((site: any) => String(site.site_id) === String(formData.site_id))) {
        setResolvedSiteMeta(null);
        return;
      }
      try {
        const response = await backendApi.getSite(String(formData.site_id), true);
        if (response?.success && response.site) {
          setResolvedSiteMeta({
            site_name: response.site.site_name,
            site_code: response.site.site_code,
          });
        }
      } catch (error) {
}
    };

    void fetchSiteMetaById();
  }, [formData.site_id, sites]);

  useEffect(() => {
    if (!id || moduleCatalog.length === 0 || loading) {
      return;
    }
    void refreshModuleAccess();
  }, [id, moduleCatalog, formData.company_id, formData.site_id, formData.role, loading]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.username.trim()) nextErrors.username = 'Username is required';
    if (!formData.email.trim()) nextErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) nextErrors.email = 'Invalid email format';
    if (['site_admin', 'general_user'].includes(normalizedRole) && !formData.site_id) nextErrors.site_id = 'Site is required';
    if (['ho_admin', 'company_admin'].includes(normalizedRole) && !formData.company_id) nextErrors.company_id = 'Company is required';
    if (formData.new_password) {
      if (formData.new_password.length < 6) {
        nextErrors.new_password = 'Password must be at least 6 characters long';
      } else if (formData.new_password !== formData.confirm_new_password) {
        nextErrors.confirm_new_password = 'Passwords do not match';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setApiError(null);
    try {
      const payload: any = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role: formData.role,
        is_active: formData.is_active,
        country_code: formData.country_code.trim() || null,
        phone_number: formData.phone_number.trim() || null,
        company_id: ['system_admin', 'super_admin'].includes(normalizedRole) ? null : (formData.company_id || null),
      };

      payload.site_id = ['site_admin', 'general_user'].includes(normalizedRole) ? formData.site_id || null : null;

      if (formData.new_password && formData.new_password.trim().length >= 6) {
        payload.new_password = formData.new_password.trim();
      }

      const response = await backendApi.updateUser(Number(id), payload);
      if (!response.success) {
        const errMsg = response.error || response.message || 'Failed to update user';
        setApiError(errMsg);
        if (errMsg.toLowerCase().includes('username')) {
          setErrors(prev => ({ ...prev, username: errMsg }));
        } else if (errMsg.toLowerCase().includes('email')) {
          setErrors(prev => ({ ...prev, email: errMsg }));
        } else if (errMsg.toLowerCase().includes('password')) {
          setErrors(prev => ({ ...prev, new_password: errMsg }));
        }
        return;
      }

      const constrainedSelection = availableUserModules.reduce((acc, module) => {
        acc[module.module_key] = !!userModuleSelection[module.module_key];
        return acc;
      }, {} as Record<string, boolean>);

      // Apply custom overrides for any role (not just general_user) when custom access is enabled
      if (Number(id) > 0) {
        if (applyCustomUserModuleAccess && Object.keys(constrainedSelection).length > 0) {
          await applyUserOverrides(backendApi, {
            userId: Number(id),
            companyId: formData.company_id || null,
            siteId: formData.site_id || null,
            moduleSelections: constrainedSelection,
          });
        } else if (!applyCustomUserModuleAccess && userOverrideEntries.length > 0) {
          // Clear overrides when custom access is turned off
          await Promise.all(userOverrideEntries.map((entry) => (
            backendApi.upsertNavigationUserOverride({
              user_id: Number(id),
              permission_key: entry.permission_key,
              scope_level: 'user',
              company_id: formData.company_id || null,
              site_id: formData.site_id || null,
              effect: entry.effect,
              reason: 'Cleared from admin user form',
              is_active: false,
            })
          )));
        }
      }

      navigate('/admin/users');
    } catch (error: any) {
      setApiError(error.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading user...</p>
        </div>
      </div>
    );
  }

  const matchedSite = sites.find((site: any) => String(site.site_id) === String(formData.site_id)) || null;
  const matchedCompany = companies.find((company: any) => String(company.company_id) === String(formData.company_id)) || null;
  const resolvedFullName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || userData?.full_name || formData.username;
  const resolvedRoleLabel = userData?.role_display || userData?.role_name || formData.role || 'user';
  const resolvedSiteName =
    (typeof userData?.site === 'string' ? userData.site : null) ||
    userData?.site?.site_name ||
    userData?.site_name ||
    matchedSite?.site_name ||
    resolvedSiteMeta?.site_name ||
    null;
  const resolvedSiteCode = userData?.site?.site_code || userData?.site_code || matchedSite?.site_code || resolvedSiteMeta?.site_code || null;
  const resolvedCompanyName = userData?.company?.company_name || userData?.company_name || userData?.site?.company_name || matchedCompany?.company_name || null;
  const resolvedCompanyCode = userData?.company?.company_code || userData?.site?.company_code || matchedCompany?.company_code || null;

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-hero p-4 sm:p-4 lg:p-5"
        >
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">User access profile</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  {isViewMode ? <Eye size={22} /> : <Edit size={22} />}
                </div>
                <div>
                  <h1 className="admin-page-title">{resolvedFullName}</h1>
                  <p className="admin-page-subtitle mt-2">
                    {isViewMode ? 'User details, access scope, activity, and assigned modules.' : 'Edit identity, role mapping, scope, and custom module access.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/admin/users')} className="admin-button admin-button-ghost">
                <ArrowLeft size={16} />
                Back to Users
              </button>
              {isViewMode && (
                <button onClick={() => navigate(`/admin/users/${id}/edit`)} className="admin-button admin-button-primary">
                  <Edit size={16} />
                  Edit User
                </button>
              )}
            </div>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Role</p>
              <p className="admin-stat-value">{resolvedRoleLabel}</p>
              <p className="admin-stat-hint">Assigned user role</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Status</p>
              <p className="admin-stat-value">{formData.is_active ? 'Active' : 'Inactive'}</p>
              <p className="admin-stat-hint">Account activation state</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Assigned Modules</p>
              <p className="admin-stat-value">{effectiveModuleKeys.length}</p>
              <p className="admin-stat-hint">Effective visible modules</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Scope</p>
              <p className="admin-stat-value">{resolvedSiteCode || resolvedCompanyCode || 'Global'}</p>
              <p className="admin-stat-hint">{resolvedCompanyName || 'System scope'}</p>
            </div>
          </div>
        </motion.section>

        {isViewMode ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="admin-section">
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">Profile Overview</h2>
                    <p className="admin-section-copy">Identity, contact details, and account state for this user.</p>
                  </div>
                </div>
                <div className="admin-section-body grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="admin-row"><div className="admin-row-icon"><User size={16} /></div><div><p className="admin-meta-label">Username</p><p className="admin-meta-value">@{formData.username}</p></div></div>
                  <div className="admin-row"><div className="admin-row-icon"><Mail size={16} /></div><div><p className="admin-meta-label">Email</p><p className="admin-meta-value break-all">{formData.email || 'Not provided'}</p></div></div>
                  <div className="admin-row"><div className="admin-row-icon"><User size={16} /></div><div><p className="admin-meta-label">Full Name</p><p className="admin-meta-value">{resolvedFullName}</p></div></div>
                  <div className="admin-row"><div className="admin-row-icon"><Phone size={16} /></div><div><p className="admin-meta-label">Phone</p><p className="admin-meta-value">{`${formData.country_code || ''} ${formData.phone_number || ''}`.trim() || 'Not provided'}</p></div></div>
                  <div className="admin-row"><div className="admin-row-icon"><Shield size={16} /></div><div><p className="admin-meta-label">Role</p><p className="admin-meta-value">{resolvedRoleLabel}</p></div></div>
                  <div className="admin-row"><div className="admin-row-icon">{formData.is_active ? <CheckCircle size={16} /> : <XCircle size={16} />}</div><div><p className="admin-meta-label">Account Status</p><p className="admin-meta-value">{formData.is_active ? 'Active' : 'Inactive'}</p></div></div>
                </div>
              </motion.section>

              <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">Access Scope</h2>
                    <p className="admin-section-copy">Company and site assignments that define this user's access boundary.</p>
                  </div>
                </div>
                <div className="admin-section-body grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="admin-row"><div className="admin-row-icon"><Building2 size={16} /></div><div><p className="admin-meta-label">Company</p><p className="admin-meta-value">{resolvedCompanyName || 'Not assigned'}{resolvedCompanyCode ? ` (${resolvedCompanyCode})` : ''}</p></div></div>
                  <div className="admin-row"><div className="admin-row-icon"><Building2 size={16} /></div><div><p className="admin-meta-label">Site</p><p className="admin-meta-value">{resolvedSiteName || 'Not assigned'}{resolvedSiteCode ? ` (${resolvedSiteCode})` : ''}</p></div></div>
                  <div className="admin-row"><div className="admin-row-icon"><ShieldCheck size={16} /></div><div><p className="admin-meta-label">Access Source</p><p className="admin-meta-value">{moduleAccessSource === 'user' ? 'Custom user overrides' : moduleAccessSource === 'site' ? 'Site role policy' : moduleAccessSource === 'company' ? 'Company subscription scope' : moduleAccessSource === 'system' ? 'System-wide access' : 'No module policy found'}</p></div></div>
                  <div className="admin-row"><div className="admin-row-icon"><Lock size={16} /></div><div><p className="admin-meta-label">Account Lock</p><p className="admin-meta-value">{isAccountLocked() ? 'Locked' : 'Not locked'}</p></div></div>
                </div>
              </motion.section>

              <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="admin-section">
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">Assigned Modules</h2>
                    <p className="admin-section-copy">Effective modules currently visible to this user based on inheritance and overrides.</p>
                  </div>
                </div>
                <div className="admin-section-body space-y-3">
                  {loadingModuleConfig ? (
                    <div className="text-sm text-gray-500 py-4">Loading assigned modules...</div>
                  ) : effectiveModuleKeys.length > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {effectiveModuleKeys.map((moduleKey) => {
                          const moduleMeta = moduleCatalog.find((module) => module.module_key === moduleKey);
                          return (
                            <span key={moduleKey} className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F4FF] text-[#0B4F82] px-2.5 py-1 text-xs font-bold border border-[#CFE8FF]">
                              <ShieldCheck size={12} />
                              {moduleMeta?.module_name || moduleKey}
                            </span>
                          );
                        })}
                      </div>
                      {overrideDeniedModuleKeys.length > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                          <p className="text-xs font-semibold text-amber-800">Restricted By Custom Override</p>
                          <p className="text-xs text-amber-700 mt-1">{overrideDeniedModuleKeys.join(', ')}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="admin-empty !py-8">No modules assigned to this user.</div>
                  )}
                </div>
              </motion.section>
            </div>

            <div className="space-y-6">
              <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="admin-section">
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">Activity</h2>
                    <p className="admin-section-copy">Created and login metadata.</p>
                  </div>
                </div>
                <div className="admin-section-body space-y-3">
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-[#5B708B]">Account Created</p>
                    <p className="text-sm font-medium text-[#0B1A2F]">{formatDate(userData?.created_at)}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-[#5B708B]">Last Login</p>
                    <p className="text-sm font-medium text-[#0B1A2F]">{formatDate(userData?.last_login)}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-[#5B708B]">Last Login IP</p>
                    <p className="text-sm font-medium text-[#0B1A2F] break-all">{userData?.last_login_ip || 'Not available'}</p>
                  </div>
                </div>
              </motion.section>

              <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">Security</h2>
                    <p className="admin-section-copy">Current lock and failed login information.</p>
                  </div>
                </div>
                <div className="admin-section-body space-y-3">
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-[#5B708B]">Failed Login Attempts</p>
                    <p className="text-sm font-medium text-[#0B1A2F]">{userData?.failed_login_attempts || 0}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-[#5B708B]">Last Failed Login</p>
                    <p className="text-sm font-medium text-[#0B1A2F]">{formatDate(userData?.last_failed_login)}</p>
                  </div>
                  {isAccountLocked() && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <p className="text-sm font-semibold text-red-700">Account is locked</p>
                      <p className="text-xs text-red-600 mt-1">Until: {formatDate(userData?.account_locked_until)}</p>
                      {userData?.locked_reason && <p className="text-xs text-red-600 mt-1">Reason: {userData.locked_reason}</p>}
                    </div>
                  )}
                </div>
              </motion.section>
            </div>
          </div>
        ) : (
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">User edit form</h2>
                <p className="admin-section-copy">Update profile fields, scope assignments, and user-specific module access where allowed.</p>
              </div>
            </div>
            <div className="admin-section-body !p-4 sm:!p-5 lg:!p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {apiError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between text-red-700 text-sm">
                    <div className="flex items-center gap-2">
                      <XCircle size={18} className="text-red-500 shrink-0" />
                      <span>{apiError}</span>
                    </div>
                    <button type="button" onClick={() => setApiError(null)} className="text-red-400 hover:text-red-600 font-bold ml-2">×</button>
                  </div>
                )}
                <div className="bg-[#F2F8FC]/50 p-4 rounded-lg border border-[#E3F0F9]">
                  <label className="block text-sm font-semibold text-[#0D2747] mb-2 flex items-center gap-2">
                    <Building2 size={18} className="text-[#0066A3]" />
                    Assigned Company
                    {['ho_admin', 'company_admin'].includes(normalizedRole) && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <select
                      title="Assigned Company"
                      value={formData.company_id}
                      onChange={(e) => {
                        const nextCompanyId = e.target.value;
                        setFormData((prev) => ({ ...prev, company_id: nextCompanyId, site_id: '' }));
                        setErrors((prev) => ({ ...prev, company_id: '' }));
                      }}
                      disabled={!canEditCompany}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent text-gray-900 bg-white ${errors.company_id ? 'border-red-500' : 'border-[#C2DFF1]'} ${!canEditCompany ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">{['ho_admin', 'company_admin'].includes(normalizedRole) ? 'Select Company' : 'All Companies'}</option>
                      {companies.map((company) => (
                        <option key={company.company_id} value={company.company_id}>
                          {company.company_name} ({company.company_code})
                        </option>
                      ))}
                    </select>
                    {loadingCompanies && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#0066A3]">Loading...</div>}
                  </div>
                  {errors.company_id && <p className="text-red-500 text-sm mt-1">{errors.company_id}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Username <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 ${errors.username ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Country Code</label>
                    <input
                      type="text"
                      value={formData.country_code}
                      onChange={(e) => setFormData((prev) => ({ ...prev, country_code: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                      placeholder="+91"
                      maxLength={10}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => setFormData((prev) => ({ ...prev, phone_number: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                      placeholder="1234567890"
                      maxLength={20}
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-2 border-t border-gray-100">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Lock size={16} className="text-[#0066A3]" />
                      Change Password <span className="text-xs text-gray-500 font-normal">(Leave blank to keep current password)</span>
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.new_password}
                          onChange={(e) => {
                            setFormData((prev) => ({ ...prev, new_password: e.target.value }));
                            setErrors((prev) => ({ ...prev, new_password: '' }));
                          }}
                          placeholder="Min 6 characters"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 pr-16 ${errors.new_password ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xs font-medium"
                        >
                          {showPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      {errors.new_password && <p className="text-red-500 text-sm mt-1">{errors.new_password}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.confirm_new_password}
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, confirm_new_password: e.target.value }));
                          setErrors((prev) => ({ ...prev, confirm_new_password: '' }));
                        }}
                        placeholder="Repeat new password"
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 ${errors.confirm_new_password ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.confirm_new_password && <p className="text-red-500 text-sm mt-1">{errors.confirm_new_password}</p>}
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-2 border-t border-gray-100">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Shield size={16} className="text-[#0066A3]" />
                      Access & Permissions
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Role <span className="text-red-500">*</span></label>
                      <select
                        title="Select User Role"
                        value={formData.role}
                        onChange={(e) => {
                          const nextRole = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            role: nextRole,
                            site_id: ['ho_admin', 'company_admin', 'system_admin', 'super_admin'].includes(nextRole) ? '' : prev.site_id,
                          }));
                          if (nextRole !== 'general_user') {
                            setApplyCustomUserModuleAccess(false);
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent text-gray-900 bg-white"
                      >
                        {roles.length > 0 ? roles.map((role) => (
                          <option key={role.role_id} value={role.role_name}>{role.display_name}</option>
                        )) : (
                          <>
                            <option value="general_user">General User</option>
                            <option value="site_admin">Site Admin</option>
                            <option value="ho_admin">HO Admin</option>
                            <option value="system_admin">System Admin</option>
                          </>
                        )}
                      </select>
                    </div>

                    {roleRequiresSite && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Site <span className="text-red-500">*</span></label>
                        <select
                          title="Select Site"
                          value={formData.site_id}
                          onChange={(e) => {
                            setFormData((prev) => ({ ...prev, site_id: e.target.value }));
                            setErrors((prev) => ({ ...prev, site_id: '' }));
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent text-gray-900 bg-white ${errors.site_id ? 'border-red-500' : 'border-gray-300'}`}
                        >
                          <option value="">Select a site</option>
                          {sites
                            .filter((site) => !formData.company_id || String(site.company_id) === String(formData.company_id))
                            .map((site) => (
                              <option key={site.site_id} value={site.site_id}>{site.site_name} ({site.site_code})</option>
                            ))}
                        </select>
                        {errors.site_id && <p className="text-red-500 text-sm mt-1">{errors.site_id}</p>}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 border border-[#E3F0F9] rounded-lg p-4 bg-[#F2F8FC]/40">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-xl font-black text-black">Assigned Modules</h3>
                      <p className="text-base font-extrabold text-gray-900">
                        Select which modules this user can access from the inherited scope.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-lg font-black text-black cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={applyCustomUserModuleAccess}
                        onChange={(e) => {
                          setApplyCustomUserModuleAccess(e.target.checked);
                          // When disabling custom access, reset to all inherited modules enabled
                          if (!e.target.checked) {
                            const resetMap: Record<string, boolean> = {};
                            availableUserModules.forEach((module) => {
                              resetMap[module.module_key] = true;
                            });
                            setUserModuleSelection((prev) => ({ ...prev, ...resetMap }));
                          }
                        }}
                        className="w-4 h-4"
                      />
                      Customize access
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const allEnabled: Record<string, boolean> = {};
                        availableUserModules.forEach((module) => {
                          allEnabled[module.module_key] = true;
                        });
                        setUserModuleSelection((prev) => ({ ...prev, ...allEnabled }));
                        setApplyCustomUserModuleAccess(true);
                      }}
                      disabled={availableUserModules.length === 0}
                      className="px-3 py-1.5 text-sm font-black border-2 border-[#0066A3] text-[#1C4D88] rounded-lg hover:bg-[#F2F8FC] disabled:opacity-50"
                    >
                      Enable All
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const allDisabled: Record<string, boolean> = {};
                        availableUserModules.forEach((module) => {
                          allDisabled[module.module_key] = false;
                        });
                        setUserModuleSelection((prev) => ({ ...prev, ...allDisabled }));
                        setApplyCustomUserModuleAccess(true);
                      }}
                      disabled={availableUserModules.length === 0}
                      className="px-3 py-1.5 text-sm font-black border-2 border-red-600 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      Disable All
                    </button>
                    {applyCustomUserModuleAccess && (
                      <span className="px-3 py-1.5 text-xs font-semibold bg-amber-50 border border-amber-300 text-amber-700 rounded-lg">
                        Custom access active — only selected modules will be saved
                      </span>
                    )}
                  </div>

                  {loadingModuleConfig ? (
                    <div className="text-sm text-gray-500 py-3">Loading assigned modules...</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {availableUserModules.map((module) => (
                        <label
                          key={module.module_key}
                          className={`flex items-center justify-between gap-2 border rounded-md px-3 py-2 cursor-pointer transition-colors ${
                            applyCustomUserModuleAccess
                              ? userModuleSelection[module.module_key]
                                ? 'border-[#8AC4E6] bg-[#F2F8FC]'
                                : 'border-gray-200 bg-white opacity-60'
                              : 'border-green-200 bg-green-50'
                          }`}
                        >
                          <span className="text-base font-black text-black">{module.module_name}</span>
                          <input
                            type="checkbox"
                            checked={applyCustomUserModuleAccess ? !!userModuleSelection[module.module_key] : true}
                            disabled={!applyCustomUserModuleAccess}
                            onChange={(e) => {
                              setUserModuleSelection((prev) => ({
                                ...prev,
                                [module.module_key]: e.target.checked,
                              }));
                            }}
                            className="w-4 h-4 accent-[#0066A3]"
                          />
                        </label>
                      ))}
                    </div>
                  )}

                  {availableUserModules.length === 0 && !loadingModuleConfig && (
                    <p className="text-sm font-bold text-amber-800 mt-2">No modules available from selected company/site scope.</p>
                  )}
                  {applyCustomUserModuleAccess && overrideDeniedModuleKeys.length > 0 && (
                    <p className="text-xs text-amber-700">Previously restricted: {overrideDeniedModuleKeys.join(', ')}</p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4 text-[#0084CF] border-gray-300 rounded focus:ring-[#0084CF]"
                    />
                    <span className="text-sm font-medium text-gray-700">User is active</span>
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-5 border-t border-gray-200">
                  <button type="button" onClick={() => navigate('/admin/users')} className="admin-button admin-button-ghost flex-1 justify-center">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="admin-button admin-button-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                    <Save size={18} />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
};

export default UserEdit;

